<script setup>
import { useAlert } from 'dashboard/composables';
import AddMessageTemplate from './AddMessageTemplate.vue';
import EditMessageTemplate from './EditMessageTemplate.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import { computed, onMounted, ref, defineOptions } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

defineOptions({ name: 'MessageTemplatesSettings' });

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { getPlainText } = useMessageFormatter();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeTemplate = ref({});
const loading = ref({});
const searchQuery = ref('');

const records = computed(() => getters.getMessageTemplates.value);
const uiFlags = computed(() => getters.getMessageTemplateUIFlags.value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'name', weight: 4 },
    'content',
  ]);
});

const tableHeaders = computed(() => [
  t('MESSAGE_TEMPLATES.LIST.TABLE_HEADER.NAME'),
  t('MESSAGE_TEMPLATES.LIST.TABLE_HEADER.CATEGORY'),
  t('MESSAGE_TEMPLATES.LIST.TABLE_HEADER.ACTIONS'),
]);

const categoryLabel = category =>
  t(`MESSAGE_TEMPLATES.CATEGORIES.${category || 'UTILITY'}`);

const deleteConfirmText = computed(
  () =>
    `${t('MESSAGE_TEMPLATES.DELETE.CONFIRM.YES')} ${activeTemplate.value.name}`
);
const deleteRejectText = computed(
  () =>
    `${t('MESSAGE_TEMPLATES.DELETE.CONFIRM.NO')} ${activeTemplate.value.name}`
);
const deleteMessage = computed(() => ` ${activeTemplate.value.name} ? `);

onMounted(() => {
  store.dispatch('getMessageTemplates');
});

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};
const openEditPopup = template => {
  activeTemplate.value = template;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};
const openDeletePopup = template => {
  activeTemplate.value = template;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  loading.value[activeTemplate.value.id] = true;
  closeDeletePopup();
  try {
    await store.dispatch('deleteMessageTemplate', activeTemplate.value.id);
    useAlert(t('MESSAGE_TEMPLATES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('MESSAGE_TEMPLATES.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[activeTemplate.value.id] = false;
    activeTemplate.value = {};
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('MESSAGE_TEMPLATES.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('MESSAGE_TEMPLATES.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('MESSAGE_TEMPLATES.HEADER')"
        :description="$t('MESSAGE_TEMPLATES.DESCRIPTION')"
        :search-placeholder="$t('MESSAGE_TEMPLATES.SEARCH_PLACEHOLDER')"
        feature-name="message_templates"
      >
        <template v-if="records.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('MESSAGE_TEMPLATES.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('MESSAGE_TEMPLATES.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="searchQuery ? $t('MESSAGE_TEMPLATES.NO_RESULTS') : ''"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="template in items"
            :key="template.id"
            :item="template"
          >
            <template #default>
              <BaseTableCell class="max-w-0">
                <div class="flex flex-col gap-2 min-w-0">
                  <span class="text-heading-3 text-n-slate-12 truncate block">
                    {{ template.name }}
                  </span>
                  <p class="text-body-main text-n-slate-11 line-clamp-3">
                    {{ getPlainText(template.content) }}
                  </p>
                </div>
              </BaseTableCell>
              <BaseTableCell class="w-36">
                <span class="text-body-main text-n-slate-11">
                  {{ categoryLabel(template.category) }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end" class="w-24">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="$t('MESSAGE_TEMPLATES.EDIT.BUTTON_TEXT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEditPopup(template)"
                  />
                  <Button
                    v-tooltip.top="$t('MESSAGE_TEMPLATES.DELETE.BUTTON_TEXT')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[template.id]"
                    @click="openDeletePopup(template)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddMessageTemplate :on-close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditMessageTemplate
        v-if="showEditPopup"
        :id="activeTemplate.id"
        :ed-name="activeTemplate.name"
        :ed-content="activeTemplate.content"
        :ed-category="activeTemplate.category"
        :ed-language="activeTemplate.language"
        :on-close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('MESSAGE_TEMPLATES.DELETE.CONFIRM.TITLE')"
      :message="$t('MESSAGE_TEMPLATES.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
